import React, { useState, useEffect, useRef } from "react";
import ResponsiveOverlay from "@/components/ResponsiveOverlay/ResponsiveOverlay";
import logoOrangeIcon from "../../assets/logo-orange.svg";
import qrCodeImage from "../../assets/images/qr_code_popover.png";
import travelerBackground from "../../assets/images/traveller_background.png";
import Button from "../Buttons/Button/Button";
import { WebAnalytics } from "@/lib/analytics";
import { EVENT_LOGGED_OUT_AUTH_CTA_DISMISSED } from "@/lib/analyticsEvents";
import { useLocation } from 'react-router-dom';
import { isDesktop } from "@/utils/isDesktop";
import { isTablet } from "@/utils/isTablet";
import "./SignUpCTAPopover.css";

interface Props {
  onClose: () => void;
  title?: string;
  description?: string;
  redirectPath?: string;
  onSignUpClick?: () => void;
}

const SignUpCTAPopover: React.FC<Props> = ({ 
  onClose, 
  title = "Sign up to unlock this feature",
  description = "Create an account to access all of Globe Thrivers' features including AI-powered trip planning, saving posts, following creators, and more.",
  redirectPath: _redirectPath,
  onSignUpClick
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const pathname = useLocation().pathname;
  const showTimeRef = useRef<number>(Date.now());
  const dismissedTrackedRef = useRef(false);

  const handleClose = () => {
    // Track dismissal if not already tracked
    if (!dismissedTrackedRef.current) {
      dismissedTrackedRef.current = true;
      const timeVisible = Math.round((Date.now() - showTimeRef.current) / 1000);
      const deviceType = isDesktop() ? 'desktop' : isTablet() ? 'tablet' : 'mobile';
      const pageName = pathname || 'unknown';
      
      WebAnalytics.trackLoggedOutInteraction(
        EVENT_LOGGED_OUT_AUTH_CTA_DISMISSED,
        pageName,
        {
          time_visible_seconds: timeVisible,
          device_type: deviceType,
        }
      );
    }
    
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 150);
  };
  
  // Reset tracking when component mounts
  useEffect(() => {
    showTimeRef.current = Date.now();
    dismissedTrackedRef.current = false;
  }, []);

  const handleSignUp = () => {
    // Close this popover first
    handleClose();
    // Then notify parent to open LoginModal after a short delay
    if (onSignUpClick) {
      setTimeout(() => {
        onSignUpClick();
      }, 200);
    }
  };

  return (
    <ResponsiveOverlay
      onClose={handleClose}
      isClosing={isClosing}
      animate
      presentation="sheet"
      unwrapped
      showSheetHandle
      keyboardAware
      className="sign-up-cta-popover-overlay"
      aria-label={title}
    >
      <div
        className="mx-auto w-full max-w-[600px] px-4"
        onClick={(e) => e.stopPropagation()}
      >
      <div
        className={`sign-up-cta-popover ${isClosing ? 'closing' : ''}`}
      >
        <button className="sign-up-cta-popover-close" onClick={handleClose} aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Hero Image Section */}
        <div className="sign-up-cta-popover-hero">
          <img
            src={travelerBackground}
            alt="Travel background"
            className="sign-up-cta-popover-hero-image"
          />
          <div className="sign-up-cta-popover-gradient"></div>
          <div className="sign-up-cta-popover-logo-overlay">
            <img src={logoOrangeIcon} alt="Globe Thrivers" className="sign-up-cta-popover-logo" />
          </div>
        </div>

        {/* Content Section */}
        <div className="sign-up-cta-popover-content">

          <h2 className="sign-up-cta-popover-title">
            {title}
          </h2>

          <p className="sign-up-cta-popover-description">
            {description}
          </p>

          <div className="sign-up-cta-popover-divider sign-up-cta-popover-divider--desktop"></div>

          {/* QR Code Section - Desktop Only */}
          <div className="sign-up-cta-popover-qr-section">
            <div className="sign-up-cta-popover-qr-text">
              <strong>Get the full experience on mobile</strong>
              <p>Scan the QR code to download the Globe Thrivers app and access all features on the go.</p>
            </div>
            <div className="sign-up-cta-popover-qr-code">
              <img src={qrCodeImage} alt="QR Code" className="qr-code-image" />
            </div>
          </div>

          <div className="sign-up-cta-popover-divider sign-up-cta-popover-divider--desktop"></div>

          {/* Sign Up Button */}
          <div className="sign-up-cta-popover-button-container">
            <Button
              variant="primary"
              onClick={handleSignUp}
              className="sign-up-cta-popover-button"
              fullWidth
            >
              Log In or Sign Up
            </Button>
          </div>
        </div>
      </div>
      </div>
    </ResponsiveOverlay>
  );
};

export default SignUpCTAPopover;

